import React, {
    useMemo,
    useCallback,
    useEffect,
    useState,
    useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import {Box, CircularProgress, Tab, Typography} from '@material-ui/core';
import { DataTable } from '../../../contexts/dataTable';
import VppBtnGroup from '../../../components/BtnGroup/VppBtnGroup';
import { getActivities } from '../../../actions/activities.actions';
import { dateFormatter, timeFormatter } from '../../../utils';
import axios from 'axios';
import {TabContext, TabList, TabPanel} from "@material-ui/lab";
import moment from 'moment';

const sortableColumns = [
    'date',
    'time',
    'permit_type',
    'activity_type',
    'old_value',
    'value',
    'details',
];


const defaultPagination = {
    page: 1,
    page_size: 10
};

const ReportsPage = () => {
    const [pagination, setPagination] = useState(defaultPagination);
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTab, setSelectedTab] = useState("1");

    const { data, paging } = useSelector((state) => state.activities);
    const { register, setValue, handleSubmit, getValues } = useForm();
    const dispatch = useDispatch();

    const cancelSearch = useRef(null);
    // The search term actually applied to the last fetch. `search` alone is the
    // typed-but-not-yet-submitted value (Search.js fires handleSearch on every
    // change when isAutocomplete), so paging/sorting refetches must read this,
    // not `search`.
    const appliedSearch = useRef('');
    // Ignore finally() from cancelled/superseded fetches so isLoading cannot
    // flip false while a newer request is still in flight.
    const fetchGeneration = useRef(0);

    // Function to check if a value is a valid date and format it
    const formatValueAsDate = (value) => {
        if (!value) return value;
        
        // Try to parse the value as a UTC date
        const momentDate = moment.utc(value);
        if (momentDate.isValid()) {
            // Convert from UTC to America/Chicago timezone
            return momentDate.tz('America/Chicago').format('MM/DD/YYYY HH:mm');
        }
        return value;
    };

    const columns = useMemo(
        () => [
            {
                id: 'unit',
                Header: 'Unit #',
                accessor: 'unit_number',
                width: 50,
            },
            {
                id: 'date',
                Header: 'Date',
                accessor: 'date',
                Cell({ value }) {
                    return dateFormatter(value);
                },
            },
            {
                id: 'time',
                Header: 'Time',
                accessor: 'time',
                Cell({ value }) {
                    return timeFormatter(value);
                },
            },
            {
                Header: 'Permit type',
                id: 'permit_type',
                accessor: 'permit_type',
            },
            {
                Header: 'Permit number',
                id: 'permit_number',
                accessor: 'permit_number',
            },
            {
                Header: 'Activity type',
                id: 'activity_type',
                accessor: 'activity_type',
            },
            {
                Header: 'Old value',
                id: 'old_value',
                accessor: 'old_value',
                Cell({ value }) {
                    return formatValueAsDate(value);
                },
            },
            {
                id: 'value',
                Header: 'New Value',
                accessor: 'value',
                Cell({ value }) {
                    return formatValueAsDate(value);
                },
            },
            {
                Header: 'User',
                id: 'user',
                accessor: 'user',
            },
            {
                Header: 'Details',
                id: 'details',
                accessor: 'details',
                width: 700,
            },
        ],
        []
    );

    const getData = useCallback(() => {
        if (cancelSearch.current) {
            cancelSearch.current.cancel();
        }
        cancelSearch.current = axios.CancelToken.source();
        const generation = ++fetchGeneration.current;

        const params = {
            ...pagination,
            ...sort
        };

        if (appliedSearch.current) {
            params.search = appliedSearch.current;
        }

        setIsLoading(true);
        dispatch(getActivities(params, cancelSearch.current.token))
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    throw new Error(error);
                }
            })
            .finally(() => {
                if (fetchGeneration.current === generation) {
                    setIsLoading(false);
                }
            });
    }, [sort, pagination]);

    const handleSearch = (data) => {
        setSearch(data.search || '');
    }

    useEffect(() => {
        getData();
    }, [getData]);

    // Applying/clearing a search resets to page 1; the new pagination object
    // re-creates getData and the effect above does the fetch.
    const onSearch = useCallback(() => {
        const trimmed = String(getValues('search') ?? search ?? '').trim();
        setValue('search', trimmed);
        setSearch(trimmed);
        appliedSearch.current = trimmed;
        setIsLoading(true);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, [getValues, search, setValue]);

    const handleClearSearch = useCallback(() => {
        appliedSearch.current = '';
        setValue('search', '');
        setSearch('');
        setSort({});
        setIsLoading(true);
        setPagination((prev) => ({ ...prev, page: 1 }));
    }, [setValue]);

    const onTabChange = (event, value) => {
        setSelectedTab(value);
    }

    return (
        <Box style={{ height: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column' }}>
            <TabContext value={selectedTab}>
                <Box style={{
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', paddingLeft: 20 }}>
                        <TabList onChange={onTabChange} aria-label="reports">
                            <Tab label="PERMIT HISTORY" value="1" />
                        </TabList>
                    </Box>
                    {
                        selectedTab === "1" && (
                            <Box style={{ paddingLeft: 20 }}>
                                <Box style={{ marginTop: 20, marginBottom: 20 }}>
                                    Add a licence plate number to view the history of a permit
                                </Box>
                                <VppBtnGroup
                                    handleSearch={handleSearch}
                                    search={search}
                                    handleClearSearch={handleClearSearch}
                                    register={register}
                                    handleSubmit={handleSubmit}
                                    isSearching={isLoading}
                                    isAutocomplete
                                    showButton
                                    onButtonClick={onSearch}
                                />
                            </Box>
                        )
                    }
                </Box>
                <Box style={{ flex: 1, overflow: 'auto' }}>
                    <TabPanel value="1" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box>
                            {isLoading ? (
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    justifyContent="center"
                                    mt={2}
                                    alignItems="center"
                                    height="50vh"
                                >
                                    <CircularProgress color="primary" />
                                    <Typography variant="body1" style={{ marginTop: 16 }}>
                                        Searching...
                                    </Typography>
                                </Box>
                            ) : (
                                <DataTable
                                    data={data}
                                    columns={columns}
                                    handlePaging={setPagination}
                                    paging={paging}
                                    initialState={{
                                        sortBy: [
                                            {
                                                id: 'issue_date',
                                                desc: true,
                                            },
                                        ],
                                        paginable: true,
                                    }}
                                    sortingChanged={setSort}
                                    sort={sort}
                                    sortableColumns={sortableColumns}
                                    title="Activities"
                                />
                            )}
                            {!isLoading && !data.length && appliedSearch.current ? (
                                <Typography variant="h5">No results</Typography>
                            ) : null}
                        </Box>
                    </TabPanel>
                </Box>
            </TabContext>
        </Box>
    );
};

export default ReportsPage;
